<script lang="ts">
  // Sibling message of the open thread as a mini card (spec §10.2): collapsed unless unseen;
  // opening fetches the message (cache first), marks it seen, renders it into its own shadow
  // root and offers Reply / Reply all / Forward (forward carries no attachments here).
  import { untrack } from 'svelte';
  import { api } from '$lib/api/client';
  import type { ApiAttachment } from '$lib/api/types';
  import { formatDate, t } from '$lib/i18n';
  import { initialOf } from '$lib/mail/format';
  import { buildForwardDraft, buildReplyDraft, quoteHtml } from '$lib/mail/reply';
  import { sanitizeReader, textToHtml, unblockImages } from '$lib/mail/sanitize';
  import { store } from '$lib/store';
  import type { Address, MessageRow } from '$lib/types';
  import Icon from '../ui/Icon.svelte';
  import MessageBody from './MessageBody.svelte';
  import { cacheBody, inlineParts, readCached } from './_local';

  interface ThreadMeta {
    from: Address[];
    replyTo: Address[];
    to: Address[];
    cc: Address[];
    subject: string;
    date: string;
    messageId: string | null;
    references: string[];
  }

  let { m, folder, openAll = null }: { m: MessageRow; folder: string; openAll?: boolean | null } = $props();

  const unseen = $derived(!m.flags.includes('\\Seen'));

  // Collapsed unless unseen at mount time (the user's later toggles must not be undone by a flag change).
  let open = $state(untrack(() => !m.flags.includes('\\Seen')));
  let loading = $state(false);
  let failed = $state(false);
  let html = $state<string | null>(null);
  let isHtml = $state(false);
  let blocked = $state(0);
  let meta = $state<ThreadMeta | null>(null);
  let body = $state<ReturnType<typeof MessageBody> | undefined>();
  let loadedUid = -1;

  // The reader's expand/collapse-all toggle overrides the local state whenever it changes.
  $effect(() => {
    if (openAll !== null) open = openAll;
  });

  $effect(() => {
    const isOpen = open;
    const uid = m.uid;
    if (!isOpen || loadedUid === uid) return;
    loadedUid = uid;
    untrack(() => void load(uid));
  });

  function applyBody(uid: number, type: string, content: string, atts: readonly ApiAttachment[]): void {
    if (type === 'text/html') {
      const r = sanitizeReader(content, inlineParts(folder, uid, atts));
      html = r.html;
      isHtml = true;
      blocked = r.blockedImages;
    } else {
      html = textToHtml(content);
      isHtml = false;
      blocked = 0;
    }
  }

  async function load(uid: number): Promise<void> {
    loading = true;
    failed = false;
    if (!m.flags.includes('\\Seen')) void store.act('seen', folder, [uid], undefined, { undoable: false });
    const cached = await readCached(folder, uid);
    if (cached.body) applyBody(uid, cached.body.type, cached.body.content, cached.body.attachments);
    if (store.offline) {
      loading = false;
      if (!cached.body) failed = true;
      return;
    }
    try {
      const d = await api.message(folder, uid);
      meta = {
        from: d.from ?? [],
        replyTo: d.replyTo ?? [],
        to: d.to ?? [],
        cc: d.cc ?? [],
        subject: d.subject,
        date: d.date,
        messageId: d.messageId,
        references: d.references ?? [],
      };
      if (d.body) applyBody(uid, d.body.type, d.body.content, d.attachments ?? []);
      else if (html === null) {
        html = '';
        isHtml = false;
      }
      void cacheBody(folder, uid, d);
    } catch {
      if (html === null) failed = true;
    } finally {
      loading = false;
    }
  }

  function onRendered(root: ShadowRoot): void {
    if (blocked > 0 && store.isTrusted(m.fromAddr)) {
      unblockImages(root);
      blocked = 0;
    }
  }

  function showImages(): void {
    body?.unblock();
    blocked = 0;
  }

  function quoted(): string {
    const d = formatDate(meta?.date ?? m.date);
    const n = m.fromName || m.fromAddr;
    return quoteHtml(t('quoteHeader', { d, n }), body?.text() ?? '');
  }

  function reply(all: boolean): void {
    store.openCompose(
      buildReplyDraft(
        {
          subject: meta?.subject ?? m.subject,
          from: meta?.from ?? [],
          replyTo: meta?.replyTo,
          to: meta?.to ?? [],
          cc: meta?.cc ?? [],
          messageId: meta?.messageId ?? m.messageId,
          references: meta?.references ?? m.references ?? [],
          fromAddr: m.fromAddr,
        },
        { me: store.user ?? '', all, quoted: quoted() },
      ),
    );
  }

  function forward(): void {
    store.openCompose(buildForwardDraft({ subject: meta?.subject ?? m.subject }, quoted(), folder, m.uid));
  }
</script>

<div class="titem" class:open data-uid={m.uid}>
  <button type="button" class="tmsg" aria-expanded={open} onclick={() => (open = !open)}>
    <span class="tavatar">{initialOf(m.fromName, m.fromAddr)}</span>
    <span class="tmid">
      <span class="tfrom" class:tunread={unseen}><bdi>{m.fromName || m.fromAddr}</bdi></span>
      {#if !open && m.snippet}<span class="tsnip"><bdi>{m.snippet}</bdi></span>{/if}
    </span>
    {#if m.hasAttachments}<span class="tclip"><Icon name="clip" size={13} /></span>{/if}
    <time class="ttime" datetime={m.date}>{formatDate(m.date)}</time>
    <span class="tchev" class:up={open}><Icon name="chev" size={16} /></span>
  </button>
  {#if open}
    {#if loading && html === null}
      <p class="tstate">{t('loading')}</p>
    {:else if failed && html === null}
      <p class="tstate">{t(store.offline ? 'offline' : 'netError')}</p>
    {:else if html !== null}
      {#if blocked > 0}
        <div class="timg"><span>{t('imagesBlocked')}</span><button type="button" class="tlink" onclick={showImages}>{t('showImages')}</button></div>
      {/if}
      <MessageBody bind:this={body} {html} {isHtml} variant="thread" onrendered={onRendered} />
      <div class="tacts">
        <button type="button" class="tact" onclick={() => reply(false)}><span class="flip-ltr"><Icon name="reply" size={15} /></span> {t('reply')}</button>
        <button type="button" class="tact" onclick={() => reply(true)}><span class="flip-ltr"><Icon name="reply" size={15} /></span> {t('replyAll')}</button>
        <button type="button" class="tact" onclick={forward}><span class="flip-ltr"><Icon name="forward" size={15} /></span> {t('forward')}</button>
      </div>
    {/if}
  {/if}
</div>

<style>
  /* svelte-10er1on (ThreadItem) — `.tbody` lives in MessageBody */
  .tmsg {
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background: var(--surface-subtle);
    margin-bottom: var(--space-2);
    text-align: start;
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .tmsg:hover {
    background: var(--surface-sunken)
  }

  .tavatar {
    inline-size: var(--control-md);
    block-size: var(--control-md);
    border-radius: var(--radius-pill);
    background: var(--blue-100);
    color: var(--blue-700);
    display: grid;
    place-items: center;
    font-weight: var(--weight-semibold);
    font-size: var(--text-sm);
    flex: none
  }

  :global(html.tik-dark) .tavatar {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  .tmid {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: var(--space-1)
  }

  .tfrom {
    color: var(--text-strong);
    font-size: var(--text-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .tfrom.tunread {
    font-weight: var(--weight-semibold)
  }

  .tsnip {
    color: var(--text-faint);
    font-size: var(--text-xs);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .tclip {
    color: var(--text-faint);
    display: grid;
    flex: none
  }

  .ttime {
    color: var(--text-faint);
    font-size: var(--text-xs);
    flex: none
  }

  .titem {
    display: grid
  }

  .titem.open {
    background: var(--surface-subtle);
    border-radius: var(--radius-md);
    margin-bottom: var(--space-2)
  }

  .titem.open .tmsg {
    margin-bottom: 0;
    border-end-start-radius: 0;
    border-end-end-radius: 0
  }

  .tchev {
    display: grid;
    place-items: center;
    color: var(--text-faint);
    flex: none
  }

  .tchev.up {
    transform: rotate(180deg)
  }

  .tstate {
    color: var(--text-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-body);
    padding: 0 var(--space-4) var(--space-3);
    margin: 0
  }

  .timg {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    margin: 0 var(--space-4) var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    background: var(--surface-sunken);
    color: var(--text-muted);
    font-size: var(--text-xs)
  }

  .tlink {
    color: var(--text-link);
    font-size: var(--text-xs);
    text-decoration: underline
  }

  .tacts {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
    padding: 0 var(--space-4) var(--space-4)
  }

  .tact {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-block-size: var(--control-sm);
    padding-inline: var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-body);
    font-size: var(--text-xs)
  }

  .tact:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }
</style>
